import { calculateCampaignPrice } from '@/lib/utils/campaign-price'
import { useCampaign, useCampaignActive } from '@/providers/campaign-provider'
import { useMemo } from 'react'
import { useCartMiniQuery } from '../react-query/cart/use-cart'

interface UseCampaignPriceProps {
  regularPrice: number
  comparePrice: number
  productId?: string | number
}

interface UseCampaignPriceResult {
  finalPrice: number
  originalPrice: number
  discountPercent: number
  shouldShowDiscount: boolean
  isLoading: boolean
}

/**
 * Hook tính toán giá cuối cùng của sản phẩm dựa trên regularPrice, comparePrice, campaign (buy_more_save_more) và promotion trong giỏ hàng.
 * - Nếu max_promotion_amount = 0 thì không được giảm, giữ nguyên giá gốc.
 * - Nếu max_promotion_amount > 0 thì chỉ giảm cho item có giá gốc < max_promotion_amount.
 * - Phần trăm giảm giá hiển thị không vượt quá campaignPercent (chỉ ảnh hưởng UI, không tính lại giá).
 * - Trả về giá cuối cùng, phần trăm giảm giá, trạng thái loading, v.v.
 * - Sử dụng calculateCampaignPrice utility function để đảm bảo logic đồng bộ.
 */
export function useCampaignPrice({
  regularPrice,
  comparePrice,
}: UseCampaignPriceProps): UseCampaignPriceResult {
  // Lấy thông tin giỏ hàng (bao gồm max_promotion_amount)
  const { data: cartMini, isLoading: isCartLoading } = useCartMiniQuery({
    enabled: true,
  })
  // Kiểm tra campaign "buy_more_save_more" có đang hoạt động không
  const campaignData = useCampaign('buy_more_save_more')
  const isCampaignActive = useCampaignActive('buy_more_save_more')

  // Tính toán giá cuối cùng dựa trên campaign và max_promotion_amount
  const { finalPrice, originalPrice, discountPercent, shouldShowDiscount } =
    useMemo(() => {
      // Sử dụng utility function để tính campaign price
      return calculateCampaignPrice({
        regularPrice,
        comparePrice,
        maxPromotionAmount: cartMini?.max_promotion_amount ?? 0,
        campaignPercent: (campaignData?.data?.percent ?? 0) as number,
        isCampaignActive,
      })
    }, [
      regularPrice,
      comparePrice,
      cartMini?.max_promotion_amount,
      campaignData?.data?.percent,
      isCampaignActive,
    ])

  return {
    finalPrice,
    originalPrice,
    discountPercent,
    shouldShowDiscount,
    isLoading: isCartLoading,
  }
}
